import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";
import { articleContents, getDefaultArticleContent, type ArticleContent } from "@/mocks/articleContents";
import { SITE_URL } from "@/constants";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const articleMeta = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (articleMeta) {
      document.title = articleMeta.title + " | Kakobuy Spreadsheet";
    } else {
      document.title = "Artykuł nie znaleziony | Kakobuy Spreadsheet";
    }
    return () => {
      document.title = "Kakobuy Spreadsheet 2026 | Najlepszy Polski Katalog Produktów";
    };
  }, [articleMeta]);

  if (!articleMeta) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">Artykuł nie został znaleziony</h1>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors">
              <i className="ri-arrow-left-line"></i> Powrót na stronę główną
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const content: ArticleContent = articleContents[slug] || getDefaultArticleContent(slug, articleMeta.title, articleMeta.category, articleMeta.image);

  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  // --- SEO JSON-LD ---
  const siteUrl = SITE_URL;
  const articleUrl = siteUrl + "/artykul/" + slug;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": content.title,
    "description": articleMeta.excerpt,
    "image": content.image,
    "url": articleUrl,
    "datePublished": "2026-06-01",
    "dateModified": "2026-06-21",
    "author": {
      "@type": "Person",
      "name": content.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl
    },
    "inLanguage": "pl",
    "articleSection": content.category
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Strona Główna",
        "item": siteUrl + "/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Artykuły",
        "item": siteUrl + "/"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": content.title,
        "item": articleUrl
      }
    ]
  };

  const faqSections = content.sections.filter((s) => s.isFAQ && s.faqItems && s.faqItems.length > 0);
  const hasFAQ = faqSections.length > 0;

  const faqJsonLd = hasFAQ ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqSections.flatMap((section) =>
      (section.faqItems || []).map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    )
  } : null;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      {hasFAQ && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}
      <Navbar />
      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              Strona Główna
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-400 whitespace-nowrap">Artykuły</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 font-medium whitespace-nowrap line-clamp-1">{content.title}</span>
          </nav>

          {/* Article Header */}
          <header>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">
              {content.category}
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
              {content.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-8">
              <span>Autor: {content.author}</span>
              <span>&bull;</span>
              <span>Ostatnia aktualizacja: {content.date}</span>
              <span>&bull;</span>
              <span>Czas czytania: {content.readingTime}</span>
            </div>

            <div className="rounded-2xl overflow-hidden mb-10">
              <img
                src={content.image}
                alt={content.title}
                className="w-full h-auto object-cover"
              />
            </div>
          </header>

          {/* Table of Contents */}
          <nav className="bg-background-100 rounded-2xl p-5 md:p-6 mb-10" aria-label="Spis treści">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
              Spis Treści
            </h2>
            <ul className="space-y-2">
              {content.tocItems.map((item) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                    {item.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Article Content */}
          <div className="prose-custom text-foreground-800 leading-relaxed space-y-8">
            {content.sections.map((section, idx) => (
              <div key={section.id}>
                {section.isCTA ? (
                  <div className="bg-accent-50 border border-accent-200/50 rounded-2xl p-6 text-center">
                    <p className="text-sm font-medium text-accent-800 mb-3">{section.paragraphs[0]}</p>
                    <a
                      href="https://kaspreadsheet.com/"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      Otwórz Kakobuy Spreadsheet <i className="ri-arrow-right-up-line"></i>
                    </a>
                  </div>
                ) : (
                  <section id={section.id}>
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                      {section.title}
                    </h2>
                    {section.paragraphs.map((p, pi) => (
                      <p key={pi} className="text-sm md:text-base mb-4" dangerouslySetInnerHTML={{ __html: p }}>
                      </p>
                    ))}

                    {section.listItems && (
                      <ul className="space-y-3 mb-4 list-disc pl-5">
                        {section.listItems.map((item, li) => (
                          <li key={li} className="text-sm md:text-base" dangerouslySetInnerHTML={{ __html: item }}></li>
                        ))}
                      </ul>
                    )}

                    {section.isTable && section.tableHeaders && section.tableRows && (
                      <div className="overflow-x-auto mb-6">
                        <table className="w-full text-sm border-collapse">
                          <thead>
                            <tr className="bg-primary-50">
                              {section.tableHeaders.map((h, hi) => (
                                <th key={hi} className={`text-left p-3 font-semibold text-foreground-900 ${hi === 0 ? "rounded-tl-lg" : ""} ${hi === section.tableHeaders!.length - 1 ? "rounded-tr-lg" : ""}`}>
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-background-200/70">
                            {section.tableRows.map((row, ri) => (
                              <tr key={ri} className={ri % 2 === 1 ? "bg-background-50/50" : ""}>
                                {row.map((cell, ci) => (
                                  <td key={ci} className={`p-3 ${ci === 0 ? "font-medium" : "text-foreground-600"} ${ri === section.tableRows!.length - 1 && ci === 0 ? "rounded-bl-lg" : ""} ${ri === section.tableRows!.length - 1 && ci === row.length - 1 ? "rounded-br-lg" : ""}`}>
                                    {ci === 2 && cell.includes("Codziennie") ? <span className="text-accent-600 font-medium">{cell}</span> : ci === 1 && cell.startsWith("Tak") ? <span className="text-primary-600 font-semibold">{cell}</span> : cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {section.isFAQ && section.faqItems && (
                      <div className="space-y-3">
                        {section.faqItems.map((faq, fi) => (
                          <details key={fi} className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
                            <summary className="flex items-center justify-between cursor-pointer p-4 md:p-5 text-foreground-900 font-semibold text-sm">
                              {faq.question}
                              <i className="ri-arrow-down-s-line text-lg group-open:rotate-180 transition-transform"></i>
                            </summary>
                            <div className="px-4 md:px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                              {faq.answer}
                            </div>
                          </details>
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {/* CTA between sections */}
                {idx === 1 && (
                  <div className="bg-accent-50 border border-accent-200/50 rounded-2xl p-6 text-center">
                    <p className="text-sm font-medium text-accent-800 mb-3">Gotowy na zakupy?</p>
                    <a
                      href="https://kaspreadsheet.com/"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      Otwórz Kakobuy Spreadsheet <i className="ri-arrow-right-up-line"></i>
                    </a>
                  </div>
                )}

                {idx === 3 && (
                  <div className="bg-primary-50 border border-primary-200/50 rounded-2xl p-6 text-center">
                    <p className="text-sm font-medium text-primary-800 mb-3">Przekonaj się sam - sprawdź nasz spreadsheet już teraz!</p>
                    <a
                      href="https://kaspreadsheet.com/"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      Otwórz Kakobuy Spreadsheet <i className="ri-arrow-right-up-line"></i>
                    </a>
                  </div>
                )}
              </div>
            ))}

            {/* Final CTA */}
            <div className="bg-primary-500 rounded-2xl p-8 text-center">
              <h3 className="text-xl md:text-2xl font-heading font-bold text-white mb-3">
                Gotowy, aby zacząć?
              </h3>
              <p className="text-white/80 text-sm mb-6">
                Otwórz Kakobuy Spreadsheet i odkryj tysiące produktów w jednym miejscu.
              </p>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-primary-700 font-bold text-sm hover:bg-accent-100 transition-all duration-300 whitespace-nowrap shadow-lg"
              >
                Otwórz Kakobuy Spreadsheet <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>

          {/* Internal Links */}
          <div className="mt-12 pt-8 border-t border-background-200/70">
            <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">
              Powiązane artykuły
            </h3>
            <div className="flex flex-wrap gap-3">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/artykul/${ra.slug}`}
                  className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap"
                >
                  {ra.title.length > 45 ? ra.title.slice(0, 45) + "..." : ra.title}
                </Link>
              ))}
              <Link
                to="/"
                className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap"
              >
                Strona Główna
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}